import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faEye } from '@fortawesome/pro-regular-svg-icons/faEye';
import { faEyeSlash } from '@fortawesome/pro-regular-svg-icons/faEyeSlash';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import * as moment from 'moment';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Maybe } from '@codegrade/maybe';
import { datetime, expr } from '@codegrade/utils';
import { FontAwesomeIcon } from '@/components/Icon';
import * as models from '@/models';
import { CalendarConfig } from '@/models/calendar_config';
import { CoursePermissions } from '@/permissions';
import * as utils from '@/utils';
import { DatetimePicker } from '../Editors';
import './style.less';
/** An editor for the availability of an assignment. */
export const AssignmentAvailabilityEditor = tsx
    .componentFactoryOf()
    .create({
    name: 'assignment-availability-editor',
    props: {
        availability: p.ofType().required,
        disabledReason: p.ofType().default(() => Maybe.empty()),
    },
    /** @returns The data for this component. */
    data() {
        return { oldAvailableAt: Maybe.empty() };
    },
    methods: {
        /**
         * Set the `oldAvailableAt` property.
         *
         * @param fallback If the assigment doesn't have an available at set this value will be
         * used for the property.
         */
        setOldAvailableAt(fallback) {
            this.oldAvailableAt = expr.match(this.availability, 'tag', {
                fixed: () => fallback,
                timed: ({ availableAt }) => Maybe.of(availableAt),
            });
        },
        /**
         * Emit an availability with a fixed value.
         *
         * @param value If the assignment should be available or not.
         */
        emitFixedValue(value) {
            this.emitValue({ tag: 'fixed', value });
        },
        /** Emit a timed availability with the time set to a guessed default.  */
        emitAvailableAt() {
            this.emitValue({
                tag: 'timed',
                availableAt: expr.match(this.availability, 'tag', {
                    fixed: ({ value }) => this.oldAvailableAt
                        .orDefaultLazy(() => moment.utc().add(value ? -1 : 1, 'days'))
                        // Ensure that we adhere to the start of a minute for available at.
                        .startOf('minute'),
                    timed: ({ availableAt }) => availableAt,
                }),
            });
        },
        /**
         * Emit a given availability, and store the (maybe) current available at.
         *
         * @param value The new value that should be emitted.
         */
        emitValue(value) {
            this.setOldAvailableAt(this.oldAvailableAt);
            tsx.emitOn(this, 'onInput', value);
        },
    },
    computed: {
        /** @returns The text that should used for the current value in the dropdown. */
        currentDropdownValue() {
            return expr.match(this.availability, 'tag', {
                fixed: ({ value }) => (value ? 'Always available' : 'Unavailable'),
                timed: () => 'Available at',
            });
        },
        /** @returns The dropdown node of the editor. */
        dropdownNode() {
            return (<b-dropdown block class="assignment-availability-dropdown no-center" menu-class="w-100" disabled={this.disabledReason.isJust()}>
                        <span slot="button-content">{this.currentDropdownValue}</span>
                        <b-dropdown-item onClick={() => this.emitFixedValue(true)}>
                            Always available
                        </b-dropdown-item>
                        <b-dropdown-item onClick={() => this.emitFixedValue(false)}>
                            Unavailable
                        </b-dropdown-item>
                        <b-dropdown-item onClick={() => {
                    const wasTimed = this.availability.tag === 'timed';
                    this.emitAvailableAt();
                    if (!wasTimed) {
                        this.$withRef('datetime-picker', el => {
                            el.$el.querySelector('input')?.click();
                        });
                    }
                }}>
                            Available at
                        </b-dropdown-item>
                    </b-dropdown>);
        },
        /** @returns The body node for this component. */
        bodyNode() {
            return (<div v-b-popover_hover_top={this.disabledReason.orDefault('')}>
                        {expr.match(this.availability, 'tag', {
                    fixed: () => [this.dropdownNode],
                    timed: ({ availableAt }) => [
                        this.dropdownNode,
                        <DatetimePicker class="timeframe-available-at mt-1" ref="datetime-picker" disabled={this.disabledReason.isJust()} value={Maybe.of(availableAt)} secondsBehavior="startOfMinute" calendarConfig={CalendarConfig} onInput={newAvailableAt => this.emitValue({
                                tag: 'timed',
                                availableAt: newAvailableAt.orDefault(
                                // The previous value of availableAt could be from the backend, which
                                // might not be at the start of a minute, now that we have opened it in
                                // the frontend editor we set it to the start of a minute.
                                availableAt.startOf('minute')),
                            })}/>,
                    ],
                })}
                    </div>);
        },
    },
    /** @returns The render result for the component. */
    render() {
        return <div class="assignment-availability-editor">{this.bodyNode}</div>;
    },
});
/** Component rendering the assignment availability description. */
export const AssignmentAvailabilityDescription = tsx.component({
    name: 'assignment-availability-description',
    methods: {
        /**
         * Render a row in the popover table.
         *
         * @param state The node is used to descibe the state.
         * @param description The node that is the description for the given state.
         * @returns A table row element.
         */
        renderTableRow(state, description) {
            return (<tr class="align-top">
                    <td class="text-nowrap">
                        <b>{state}</b>
                    </td>
                    <td>{description}</td>
                </tr>);
        },
    },
    /** @returns The render result for the assignment availability description. */
    render() {
        return (<table class="assignment-availability-description table my-n2">
                <tbody>
                    {this.renderTableRow('Always available', 'The assignment is always available and students will be able to see it.')}
                    {this.renderTableRow('Unavailable', 'The assignment is unavailable, only users with the "See hidden assignments" permission will be able to see it.')}
                    {this.renderTableRow('Available at', 'The assignment becomes available at a certain moment in time.')}
                </tbody>
            </table>);
    },
});
const InnerStateWithLabel = tsx.component({
    name: 'popover-with-maybe-label',
    functional: true,
    props: {
        /** The icon that should be rendered. */
        icon: p.ofType().required,
        /** The popover message that should be used. */
        popover: p(String).required,
        /** Should the label be rendered? */
        withLabel: p(Boolean).required,
        /** The label that will be rendered, if `withLabel` is `true`. */
        label: p(String).required,
    },
    /**
     * Render the inner state with label.
     *
     * @param h The function to create elements with.
     * @param ctx The context for the render.
     * @returns A element with popover, icon, and potentially label.
     */
    render(h, ctx) {
        const withLabel = ctx.props.withLabel;
        const label = Maybe.of(ctx.props.label)
            .filter(() => withLabel)
            .orDefault('');
        return (<span class={['text-nowrap', ctx.data.class, ctx.data.staticClass]} style={[ctx.data.style, ctx.data.staticStyle]} v-b-popover_top_hover_viewport={ctx.props.popover}>
                <FontAwesomeIcon icon={ctx.props.icon} class={['state-icon', 'ml-0', { 'mr-2': withLabel }]}/>
                {label}
            </span>);
    },
});
const AssignmentGradeAvailabilityViewer = tsx.component({
    name: 'assignment-grade-availability-viewer',
    functional: true,
    props: {
        /** The assignment for which the grade availability should be rendered. */
        assignment: p.ofType().required,
        /** Should a label be rendered. */
        withLabel: p(Boolean).required,
    },
    /**
     * Renders the grade availability viewer.
     *
     * @param h Vue's Hyperscript function.
     * @param ctx The render context.
     * @returns The rendered assignment grade availability.
     */
    render(h, ctx) {
        const { assignment, withLabel } = ctx.props;
        const isAvail = assignment.gradeAvailability.value;
        const synchronized = assignment.lmsName.mapOrDefault(lms => ` and synchronized to ${lms}`, '');
        const statePopover = isAvail
            ? // The `synchronized` message is prefixed with a space.
                `Whether the grades (and feedback) are published to all students${synchronized}.`
            : 'The grades of this assignment are not published.';
        return (<InnerStateWithLabel popover={statePopover} icon={isAvail ? faCheck : faXmark} label={isAvail ? 'Grades published' : 'Grades not published'} withLabel={withLabel} style={{ minWidth: '1em' }} class={[
                'assignment-grade-availability-viewer',
                'd-inline-block',
                'text-center',
                ctx.data.staticClass,
                ctx.data.class,
            ]}/>);
    },
});
const AssignmentAvailabilityViewer = tsx.component({
    name: 'assignment-availability-viewer',
    functional: true,
    props: {
        /** The assignment for which the availability should be rendered. */
        assignment: p.ofType().required,
        /** Should a label be rendered. */
        withLabel: p(Boolean).required,
    },
    /**
     * Renders the availability viewer.
     *
     * @param h Vue's Hyperscript function.
     * @param ctx The render context.
     * @returns The rendered assignment availability.
     */
    render(h, ctx) {
        const { assignment, withLabel } = ctx.props;
        const getNow = ctx.parent.$getRootInstance().$getNow;
        const availPopoverExtra = expr.match(assignment.availability, 'tag', {
            fixed: () => '',
            timed: ({ availableAt }) => {
                const now = getNow(availableAt);
                const diff = datetime.diff(now, availableAt);
                const when = diff.humanize(true, { ss: 0 /* Never display "just now" */, s: 30 });
                return availableAt.isSameOrBefore(now)
                    ? ` It became available ${when}`
                    : ` It will become available ${when}`;
            },
        });
        const isAvail = models.isAssignmentAvailable(assignment, ctx.parent.$getRootInstance().$getNow);
        const availPopoverPrefix = isAvail
            ? 'This assignment is available.'
            : 'This assignment is unavailable.';
        const statePopover = `${availPopoverPrefix}${availPopoverExtra}`;
        return (<InnerStateWithLabel popover={statePopover} icon={isAvail ? faEye : faEyeSlash} label={isAvail ? 'Available' : 'Unavailable'} style={{ minWidth: '1.2em' }} withLabel={withLabel} class={[
                'assignment-availability-viewer',
                'd-inline-block',
                'text-center',
                ctx.data.staticClass,
                ctx.data.class,
            ]}/>);
    },
});
/** Component rendering a viewer for the assignment state. */
export const AssignmentStateViewer = tsx.component({
    name: 'assignment-state-viewer',
    functional: true,
    props: {
        /**
         * The assignment for which the state (= availability + grade availability) should be
         * rendered.
         */
        assignment: p.ofType().required,
        /** Should a label be rendered. */
        withLabel: p(Boolean).default(false),
    },
    /**
     * Renders the Assignment State Viewer.
     *
     * @param h Vue's Hyperscript function.
     * @param ctx The render context.
     * @returns The rendered assignment (grade) availability.
     */
    render(h, ctx) {
        const { assignment, withLabel } = ctx.props;
        const getNow = ctx.parent.$getRootInstance().$getNow;
        const maybeCourse = assignment instanceof models.Assignment ? assignment.maybeCourse : Maybe.empty();
        // Only show the availability state if users are allowed to see hidden assignments, or if
        // for some reason this course has a non available assignment in it.
        const availabilityViewer = utils.ifOrEmpty(!assignment.hasMultipleTimeframes &&
            maybeCourse.mapOrDefault(course => course.hasPermission(CoursePermissions.can_see_hidden_assignments) ||
                course.assignments.some(a => !a.getIsAvailable(getNow)), 
            // When there is no course we just assume they are allowed to see hidden
            // assignments.
            true), () => (<AssignmentAvailabilityViewer assignment={assignment} withLabel={withLabel} class={{
                'pr-2': withLabel,
                'mr-2': true,
                'border-right': withLabel,
            }}/>));
        return (<span class={[
                'assignment-state-viewer',
                'text-nowrap',
                ctx.data.class,
                ctx.data.staticClass,
            ]} style={[ctx.data.style, ctx.data.staticStyle]}>
                {availabilityViewer}
                <AssignmentGradeAvailabilityViewer assignment={assignment} withLabel={withLabel}/>
            </span>);
    },
});
